import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { services, SupportedChainId, utils } from '@orionprotocol/sdk';

const {
  infoSchema,
  poolsConfigSchema,
  poolsInfoSchema,
  pricesWithQuoteAssetSchema,
} = services.blockchainService.schemas;

export type AssetToAddress = Partial<Record<string, string>> | undefined;
export type PricesWithQuoteAsset =
  | z.infer<typeof pricesWithQuoteAssetSchema>
  | undefined;

export type LumiaChainData = Partial<{
  chainId?: number | undefined;
  chainName?: string | undefined;
  exchangeContractAddress?: string | undefined;
  swapExecutorContractAddress?: string | undefined;
  oracleContractAddress?: string | undefined;
  matcherAddress?: string | undefined;
  orderFeePercent?: number | undefined;
  assetToAddress?: AssetToAddress;
  assetToDecimals?: Partial<Record<string, number>> | undefined;
  cexTokens?: string[] | undefined;
  assetToIcons?: Partial<Record<string, string>>;
  poolsConfig?: z.infer<typeof poolsConfigSchema>;
  poolsInfo?: z.infer<typeof poolsInfoSchema>;
  internalFeeAssets?: z.infer<typeof infoSchema>['internalFeeAssets'];
  pairs?: string[];
  prices?: Partial<Record<string, string>>;
  pricesWithQuoteAsset?: PricesWithQuoteAsset;
  gasPrice?: string;
  atomicSwapAssets?: string[];
}>;
export type LumiaState = Partial<Record<SupportedChainId, LumiaChainData>>;

const initialState: LumiaState = {};

export const lumiaSlice = createSlice({
  name: 'lumia',
  initialState,
  reducers: {
    setInfo(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: z.infer<typeof infoSchema>;
      }>,
    ) {
      state[action.payload.chainId] = {
        ...state[action.payload.chainId],
        ...action.payload.data,
      };
    },
    setPoolsConfig(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: z.infer<typeof poolsConfigSchema>;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) chainState.poolsConfig = action.payload.data;
    },
    setPairs(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: string[];
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        chainState.pairs = action.payload.data;
      } else {
        state[action.payload.chainId] = {
          pairs: action.payload.data,
        };
      }
    },
    setPoolsInfo(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: z.infer<typeof poolsInfoSchema>;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        chainState.poolsInfo = action.payload.data;
      } else {
        state[action.payload.chainId] = {
          poolsInfo: action.payload.data,
        };
      }
    },
    setPrices(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: Partial<Record<string, string>>;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        if (chainState.prices) {
          chainState.prices = Object.assign(
            chainState.prices,
            action.payload.data,
          );
        } else {
          chainState.prices = action.payload.data;
        }
      }
    },
    clearPrices(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        chainState.prices = undefined;
      }
    },
    setPricesWithQuoteAsset(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: z.infer<typeof pricesWithQuoteAssetSchema>;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        if (chainState.pricesWithQuoteAsset !== undefined) {
          chainState.pricesWithQuoteAsset = Object.assign(
            chainState.pricesWithQuoteAsset,
            action.payload.data,
          );
        } else {
          chainState.pricesWithQuoteAsset = action.payload.data;
        }
      }
    },
    clearPricesWithQuoteAsset(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        chainState.pricesWithQuoteAsset = undefined;
      }
    },
    setGasPrice(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: string;
      }>,
    ) {
      const chainState = state[action.payload.chainId];
      if (chainState) {
        chainState.gasPrice = action.payload.data;
      } else {
        state[action.payload.chainId] = {
          gasPrice: action.payload.data,
        };
      }
    },
    setAtomicSwapAssets(
      state,
      action: PayloadAction<{
        chainId: SupportedChainId;
        data: string[];
      }>,
    ) {
      if (utils.isValidChainId(action.payload.chainId)) {
        const chainState = state[action.payload.chainId];
        if (chainState) {
          chainState.atomicSwapAssets = action.payload.data;
        }
      }
    },
  },
});

export const lumiaReducer = lumiaSlice.reducer;
